import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  BookOpenCheck,
  Bot,
  FolderGit2,
  Users,
  ScrollText,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '@/features/auth/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Logo } from '@/components/Logo';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

const items: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/skills', label: 'Skills', icon: BookOpenCheck },
  { to: '/subagentes', label: 'Subagentes', icon: Bot },
  { to: '/projetos', label: 'Projetos', icon: FolderGit2 },
  { to: '/usuarios', label: 'Usuários', icon: Users },
  { to: '/auditoria', label: 'Auditoria-Logs', icon: ScrollText, adminOnly: true },
];

interface SidebarProps {
  /** Em mobile (<lg) controla o drawer. Em lg+ é ignorado (sidebar é estática). */
  open?: boolean;
  /** Chamado ao navegar/sair, pra o AppShell fechar o drawer em mobile. */
  onNavigate?: () => void;
}

export function Sidebar({ open = false, onNavigate }: SidebarProps) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  return (
    <aside
      className={clsx(
        'w-60 shrink-0 bg-surface-sidebar text-text-inverse flex flex-col',
        // Mobile (<lg): drawer fixo, escondido fora da tela quando fechado.
        'fixed inset-y-0 left-0 z-50 transform transition-transform duration-200',
        // Desktop (lg+): volta a ser estático no flex do AppShell.
        'lg:static lg:translate-x-0 lg:transition-none',
        open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      )}
      aria-hidden={!open ? 'true' : undefined}
    >
      <div className="px-5 py-6">
        <Logo variant="dark-bg" className="h-8 w-auto" />
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {items
          .filter((i) => !i.adminOnly || user?.role === 'admin')
          .map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={onNavigate}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition',
                  isActive
                    ? 'bg-white/10 text-white font-medium'
                    : 'text-white/80 hover:bg-white/5 hover:text-white',
                )
              }
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/10 space-y-2">
        <div className="px-3">
          <p className="text-xs text-white/60">Conectado</p>
          <p className="text-sm font-medium text-white truncate" title={user?.email}>
            {user?.displayName || user?.email}
          </p>
          <p className="text-xs uppercase tracking-wider text-brand-300">{user?.role}</p>
        </div>
        <button
          onClick={toggle}
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-white"
        >
          <span className="text-base">{theme === 'dark' ? '☀️' : '🌙'}</span>
          <span>Modo {theme === 'dark' ? 'claro' : 'escuro'}</span>
        </button>
        <button
          onClick={() => {
            onNavigate?.();
            logout();
          }}
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-white/80 hover:bg-red-500/15 hover:text-red-200"
        >
          <LogOut size={18} />
          <span>Sair</span>
        </button>
      </div>
    </aside>
  );
}
